import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { TrendingUp, TrendingDown, AlertTriangle, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/combobox';
import { SearchBox } from '@/components/search-box';
import { useStore } from '@/lib/store-context';
import {
  getMovement, getMasters,
  type MovementRow, type MovementCategory, type MatchMode,
} from '@/lib/inventory-api';
import { fmtINR, fmtNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

const Search$ = z.object({
  category: z.enum(['all', 'fast', 'slow', 'dead', 'new']).optional(),
  subgroup: z.string().optional(),
  brand: z.string().optional(),
  style: z.string().optional(),
  q: z.string().optional(),
  match: z.enum(['contains', 'startswith', 'similar']).optional(),
  fastDays: z.coerce.number().optional(),
  deadDays: z.coerce.number().optional(),
  page: z.coerce.number().optional(),
});

export const Route = createFileRoute('/_app/inventory/movement')({
  validateSearch: Search$,
  component: MovementPage,
});

const CATEGORY_LABELS: Record<MovementCategory, string> = {
  all: 'All Stock',
  fast: 'Fast Movers',
  slow: 'Slow Movers',
  dead: 'Dead Stock',
  new: 'New / Unsold',
};

function MovementPage() {
  const search = Route.useSearch();
  const nav = Route.useNavigate();
  const { selected: selectedStore } = useStore();

  // Support 'all' stores mode
  const store = selectedStore ?? 'all';
  const isMultiStore = selectedStore === 'all' || !selectedStore;

  const filters = {
    category: (search.category ?? 'all') as MovementCategory,
    subgroup: search.subgroup ?? null,
    brand: search.brand ?? null,
    style: search.style ?? null,
    q: search.q ?? null,
    match: (search.match ?? 'contains') as MatchMode,
    fastDays: search.fastDays ?? 7,
    deadDays: search.deadDays ?? 30,
    page: search.page ?? 1,
  };

  const setF = (patch: Partial<typeof filters>) => {
    nav({
      to: '.',
      search: (prev) => {
        const next = { ...(prev as Record<string, unknown>) };
        for (const [k, v] of Object.entries(patch)) {
          if (k === 'match') {
            // 'contains' is the default — keep the URL clean by omitting it.
            if (!v || v === 'contains') delete next[k];
            else next[k] = v;
          } else if (v == null || v === '' || v === 'all' || (k === 'page' && v === 1)) {
            delete next[k];
          } else {
            next[k] = v;
          }
        }
        return next;
      },
      replace: true,
    });
  };

  const mastersQ = useQuery({
    queryKey: ['inventory:masters'],
    queryFn: getMasters,
    staleTime: 5 * 60_000,
  });

  const movementQ = useQuery({
    queryKey: ['inventory:movement', store, filters],
    queryFn: () => getMovement({
      store: store,
      category: filters.category,
      subgroup: filters.subgroup ?? undefined,
      brand: filters.brand ?? undefined,
      style: filters.style ?? undefined,
      q: filters.q ?? undefined,
      match: filters.match,
      fastDays: filters.fastDays,
      deadDays: filters.deadDays,
      page: filters.page,
      limit: 100,
    }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const summary = movementQ.data?.summary;

  return (
    <div className="space-y-4">
      <header>
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Daleep · Inventory</p>
        <h1 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">Movement Analysis</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fast = sold in {filters.fastDays} days · Slow = {filters.fastDays + 1}–{filters.deadDays} days · Dead = {filters.deadDays}+ days
        </p>
      </header>

      {/* Summary KPIs */}
      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label="Fast Movers"
            icon={<TrendingUp className="h-4 w-4 text-emerald-600" />}
            count={summary.fastCount}
            value={summary.fastValue}
            color="emerald"
            active={filters.category === 'fast'}
            onClick={() => setF({ category: filters.category === 'fast' ? 'all' : 'fast', page: 1 })}
          />
          <KpiCard
            label="Slow Movers"
            icon={<TrendingDown className="h-4 w-4 text-amber-600" />}
            count={summary.slowCount}
            value={summary.slowValue}
            color="amber"
            active={filters.category === 'slow'}
            onClick={() => setF({ category: filters.category === 'slow' ? 'all' : 'slow', page: 1 })}
          />
          <KpiCard
            label="Dead Stock"
            icon={<AlertTriangle className="h-4 w-4 text-rose-600" />}
            count={summary.deadCount}
            value={summary.deadValue}
            color="rose"
            active={filters.category === 'dead'}
            onClick={() => setF({ category: filters.category === 'dead' ? 'all' : 'dead', page: 1 })}
          />
          <KpiCard
            label="New / Unsold"
            icon={<Package className="h-4 w-4 text-blue-600" />}
            count={summary.newCount}
            value={summary.newValue}
            color="blue"
            active={filters.category === 'new'}
            onClick={() => setF({ category: filters.category === 'new' ? 'all' : 'new', page: 1 })}
          />
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <Combobox
            label="Sub-group"
            value={filters.subgroup ?? ''}
            options={mastersQ.data?.subgroup ?? []}
            onChange={(v) => setF({ subgroup: v || null, page: 1 })}
            placeholder="All sub-groups"
            className="min-w-[160px]"
          />
          <Combobox
            label="Brand"
            value={filters.brand ?? ''}
            options={mastersQ.data?.brand ?? []}
            onChange={(v) => setF({ brand: v || null, page: 1 })}
            placeholder="All brands"
            className="min-w-[180px]"
          />
          <Combobox
            label="Article"
            value={filters.style ?? ''}
            options={mastersQ.data?.style ?? []}
            onChange={(v) => setF({ style: v || null, page: 1 })}
            placeholder="All articles"
            className="min-w-[180px]"
          />
          <SearchBox
            q={filters.q}
            onQChange={(q) => nav({ to: '.', search: (prev) => ({ ...prev, q, page: 1 }), replace: true })}
            match={filters.match}
            onMatchChange={(m) => setF({ match: m, page: 1 })}
          />
          <div className="min-w-[100px]">
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Fast (days)
            </label>
            <input
              type="number"
              min={1}
              max={90}
              value={filters.fastDays}
              onChange={(e) => setF({ fastDays: Number(e.target.value) || 7, page: 1 })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>
          <div className="min-w-[100px]">
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Dead (days)
            </label>
            <input
              type="number"
              min={7}
              max={365}
              value={filters.deadDays}
              onChange={(e) => setF({ deadDays: Number(e.target.value) || 30, page: 1 })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>
          {(filters.subgroup || filters.brand || filters.style || filters.q || filters.category !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setF({
                subgroup: null, brand: null, style: null, q: null, category: 'all', page: 1,
              })}
            >
              Clear filters
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card>
        <CardHeader className="space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {CATEGORY_LABELS[filters.category]} · {fmtNumber(movementQ.data?.total ?? 0)} variants
          </CardTitle>
        </CardHeader>
        <CardContent>
          {movementQ.isLoading && !movementQ.data && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {movementQ.error && (
            <p className="text-sm text-destructive">{(movementQ.error as Error).message}</p>
          )}
          {movementQ.data && movementQ.data.rows.length === 0 && (
            <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No items match the current filters.
            </p>
          )}
          {movementQ.data && movementQ.data.rows.length > 0 && (
            <>
              <div className={cn('max-h-[60vh] overflow-auto rounded-md border', movementQ.isFetching && 'opacity-60')}>
                <MovementTable rows={movementQ.data.rows} showStore={true} />
              </div>
              {/* Pagination */}
              {movementQ.data.total > 100 && (
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Page {filters.page} of {Math.ceil(movementQ.data.total / 100)}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={filters.page <= 1}
                      onClick={() => setF({ page: filters.page - 1 })}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={filters.page * 100 >= movementQ.data.total}
                      onClick={() => setF({ page: filters.page + 1 })}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  label, icon, count, value, color, active, onClick,
}: {
  label: string;
  icon: React.ReactNode;
  count: number;
  value: number;
  color: 'emerald' | 'amber' | 'rose' | 'blue';
  active: boolean;
  onClick: () => void;
}) {
  const bg = {
    emerald: 'bg-emerald-50 border-emerald-200',
    amber: 'bg-amber-50 border-amber-200',
    rose: 'bg-rose-50 border-rose-200',
    blue: 'bg-blue-50 border-blue-200',
  }[color];
  const activeBg = {
    emerald: 'ring-2 ring-emerald-500',
    amber: 'ring-2 ring-amber-500',
    rose: 'ring-2 ring-rose-500',
    blue: 'ring-2 ring-blue-500',
  }[color];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border p-4 text-left transition-all hover:shadow-sm',
        bg,
        active && activeBg
      )}
    >
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{fmtNumber(count)}</div>
      <div className="text-sm text-muted-foreground">{fmtINR(value)}</div>
    </button>
  );
}

function MovementTable({ rows, showStore }: { rows: MovementRow[]; showStore?: boolean }) {
  return (
    <table className="w-full text-[12px]">
      <thead className="sticky top-0 z-10 bg-sand text-[10px] uppercase tracking-wider text-muted-foreground shadow-[0_1px_0_0_hsl(var(--border))]">
        <tr>
          {showStore && <Th>Store</Th>}
          <Th>Item</Th>
          <Th>Brand</Th>
          <Th>Article</Th>
          <Th>Color</Th>
          <Th>Sub-group</Th>
          <Th align="right">Stock</Th>
          <Th align="right">MRP</Th>
          <Th align="right">SP</Th>
          <Th align="right">Value</Th>
          <Th align="right">Last Sale</Th>
          <Th align="right">Days</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={`${r.storeDb}-${r.item}-${r.brand}-${r.style}-${r.color}-${i}`}
            className={cn(
              'border-t border-border/40 transition-colors hover:bg-sand/40',
              r.category === 'dead' && 'bg-rose-50/50',
              r.category === 'slow' && 'bg-amber-50/50',
              r.category === 'fast' && 'bg-emerald-50/50',
              r.category === 'new' && 'bg-blue-50/50'
            )}
          >
            {showStore && <Td className="font-medium text-muted-foreground">{r.storeName}</Td>}
            <Td>{r.item}</Td>
            <Td className="font-medium">{r.brand}</Td>
            <Td>{r.style}</Td>
            <Td>{r.color}</Td>
            <Td>{r.subgroup}</Td>
            <Td align="right" className="font-mono-num">{fmtNumber(r.stockQty)}</Td>
            <Td align="right" className="font-mono-num">{fmtINR(r.mrp)}</Td>
            <Td align="right" className="font-mono-num">{r.sp ? fmtINR(r.sp) : '—'}</Td>
            {/* value at MRP — matches the API summary (fastValue/deadValue). `sp` is
                LASTPURRATE (cost) under stock formula v7, not a selling price. */}
            <Td align="right" className="font-mono-num">{fmtINR(r.stockQty * r.mrp)}</Td>
            <Td align="right" className="font-mono-num">
              {r.lastSaleDate ? formatDate(r.lastSaleDate) : '—'}
            </Td>
            <Td align="right" className="font-mono-num">
              {r.daysSinceLastSale ?? '—'}
            </Td>
            <Td>
              <StatusBadge category={r.category} />
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({ category }: { category: MovementRow['category'] }) {
  const styles = {
    fast: 'bg-emerald-100 text-emerald-800',
    slow: 'bg-amber-100 text-amber-800',
    dead: 'bg-rose-100 text-rose-800',
    new: 'bg-blue-100 text-blue-800',
  }[category];
  const labels = { fast: 'Fast', slow: 'Slow', dead: 'Dead', new: 'New' };
  return (
    <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium uppercase', styles)}>
      {labels[category]}
    </span>
  );
}

function formatDate(yyyymmdd: number): string {
  const s = String(yyyymmdd);
  if (s.length !== 8) return s;
  return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(2, 4)}`;
}

function Th({ children, align, className }: { children: React.ReactNode; align?: 'left' | 'right'; className?: string }) {
  return (
    <th className={cn('whitespace-nowrap px-2 py-2 text-left font-medium', align === 'right' && 'text-right', className)}>
      {children}
    </th>
  );
}

function Td({ children, align, className }: { children: React.ReactNode; align?: 'left' | 'right'; className?: string }) {
  return (
    <td className={cn('whitespace-nowrap px-2 py-1.5', align === 'right' && 'text-right', className)}>
      {children}
    </td>
  );
}
